import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import {
  getMenuById,
  getMenuDetailsById,
  getMenuItemPricePreview
} from 'catering/api/resources/menu';
import {
  useCart,
  selectMenuItemDetailsBySlug,
  selectCartItemById,
  useCateringLocationFirstAvailableFulfillmentHours
} from 'catering/hooks/query';
import { buildFormModificationsForPricePreview } from 'helpers/forms/menuItem';
import { getValueWithTwoDecimalPlace } from 'helpers/formattedDollar';
import { PRODUCT_VARIANT_FIELD_NAME } from 'constants/menu/menuItemDetailForm';

export const MENU_QUERY_KEY = 'menu';
export const MENU_DETAILS_QUERY_KEY = 'menu-details';
export const MENU_ITEM_PRICE_PREVIEW_QUERY_KEY = 'menu-item-price-preview';

const DEFAULT_QUERY_OPTIONS = {
  staleTime: 1000 * 60 * 60,
  cacheTime: 1000 * 60 * 60
};

export function useFirstAvailableMenuIdFromLocationOrCart(fulfillmentMethod) {
  const { data: cart } = useCart();
  const { locationSlug } = useParams();

  const {
    data: availableHours,
    error
  } = useCateringLocationFirstAvailableFulfillmentHours({
    locationSlug
  });

  const firstAvailableMenuId =
    availableHours?.all?.catering_fulfillment_menu_id;

  // only use the menu ID from the cart if its location slug matches the browser slug
  const menuId =
    locationSlug === cart?.location_slug && cart?.fulfillment_menu_id
      ? cart?.fulfillment_menu_id
      : firstAvailableMenuId;

  return {
    menuId,
    isNotMenuAvailable:
      !menuId && error?.message?.includes('No fulfillment hours found')
  };
}

function useMenuQuery(customQueryOptions = {}) {
  const { menuId } = useFirstAvailableMenuIdFromLocationOrCart();

  const result = useQuery([MENU_QUERY_KEY, menuId], () => getMenuById(menuId), {
    ...DEFAULT_QUERY_OPTIONS,
    ...customQueryOptions,
    enabled: !!menuId
  });

  return {
    ...result,
    isLoading: result.isIdle || result.isLoading
  };
}

export function useMenu(customQueryOptions) {
  return useMenuQuery(customQueryOptions);
}

function useMenuDetailsQuery(menuId, customQueryOptions = {}) {
  return useQuery(
    [MENU_DETAILS_QUERY_KEY, menuId],
    () => getMenuDetailsById(menuId),
    {
      ...DEFAULT_QUERY_OPTIONS,
      ...customQueryOptions,
      enabled: !!menuId
    }
  );
}

export function useMenuDetails(customQueryOptions) {
  const {
    menuId,
    isNotMenuAvailable
  } = useFirstAvailableMenuIdFromLocationOrCart();

  return {
    ...useMenuDetailsQuery(menuId, customQueryOptions),
    isNotMenuAvailable
  };
}

export function useMenuItemDetailsBySlug(menuItemSlug, customQueryOptions) {
  const { data: cart } = useCart();

  const menuId = cart?.fulfillment_menu_id;

  const result = useMenuDetailsQuery(menuId, {
    enabled: !!menuId,
    select: menuDetails =>
      selectMenuItemDetailsBySlug(menuDetails, menuItemSlug),
    ...customQueryOptions
  });

  return {
    ...result,
    isLoading: result.isIdle || result.isLoading
  };
}

export function useMenuItemDetailsByCartItemId(cartItemId, customQueryOptions) {
  const { data: cart } = useCart({
    select: cart => ({
      ...cart,
      cartItem: selectCartItemById(cart.items, cartItemId)
    })
  });

  const menuId = cart?.fulfillment_menu_id;
  const menuItemSlug = cart?.cartItem?.item_slug;

  const result = useMenuDetailsQuery(menuId, {
    enabled: !!menuId && !!menuItemSlug,
    select: menuDetails =>
      selectMenuItemDetailsBySlug(menuDetails, menuItemSlug),
    ...customQueryOptions
  });

  return {
    ...result,
    isLoading: result.isIdle || result.isLoading
  };
}

export function usePrefetchMenuDetails() {
  const { menuId } = useFirstAvailableMenuIdFromLocationOrCart();
  const queryClient = useQueryClient();
  React.useEffect(() => {
    (async () => {
      if (menuId) {
        await queryClient.prefetchQuery([MENU_DETAILS_QUERY_KEY, menuId], () =>
          getMenuDetailsById(menuId)
        );
      }
    })();
  }, [queryClient, menuId]);
}

export function useMenuItemPricePreviewQuery({
  menuItemId,
  data,
  customQueryOptions = {}
}) {
  return useQuery(
    [MENU_ITEM_PRICE_PREVIEW_QUERY_KEY, { menuItemId, ...data }],
    () => getMenuItemPricePreview(menuItemId, data),
    {
      staleTime: 0,
      cacheTime: 0,
      ...customQueryOptions
    }
  );
}

export function useMenuItemPricePreview(
  menuItemDetails,
  formState,
  modifierGroups
) {
  const { id: menuItemId, default_price } = menuItemDetails;
  const {
    quantity,
    [PRODUCT_VARIANT_FIELD_NAME]: variantId,
    ...modifications
  } = formState;

  const parsePricePreview = price => parseFloat(price).toFixed(2);

  const setParsedPricePreview = price =>
    setPricePreview(parsePricePreview(price));

  const [pricePreview, setPricePreview] = React.useState(
    parsePricePreview(default_price)
  );
  const [inventory, setInventory] = React.useState(null);
  const [lowInventoryCount, setLowInventoryCount] = React.useState(null);

  function handleMenuItemPricePreviewSuccess(menuItemPricePreview) {
    setParsedPricePreview(
      getValueWithTwoDecimalPlace(menuItemPricePreview.final_price)
    );
    setInventory(menuItemPricePreview.inventory);
    setLowInventoryCount(menuItemPricePreview.low_inventory_count);
  }

  const data = {
    product_id: menuItemId,
    quantity,
    required_fields: {
      ...buildFormModificationsForPricePreview(modifierGroups, modifications)
    }
  };

  if (variantId) {
    data.variant_id = parseInt(variantId);
  }

  useMenuItemPricePreviewQuery({
    menuItemId,
    data,
    customQueryOptions: {
      onSuccess: menuItemPricePreview =>
        handleMenuItemPricePreviewSuccess(menuItemPricePreview)
    }
  });

  return { price_preview: pricePreview, inventory, lowInventoryCount };
}

export * from 'catering/hooks/query/menu/selectors';
